import * as trace from 'owa-trace';
import CalendarEvent from 'mini-calendar-event-types/lib/MiniCalendarEvent';
import datapoints from '../datapoints';
import GetCalendarViewResponse from 'owa-service/lib/contract/GetCalendarViewResponse';
import GetUserAvailabilityInternalResponse from 'owa-service/lib/contract/GetUserAvailabilityInternalResponse';
import getUserAvailabilityService from 'mini-freebusy/lib/services/getUserAvailabilityService';
import { Calendar } from 'mini-event-to-calendar-association/lib/schema/Calendar';
import { calendarEventWithPeoplesCalendarInfo } from '../utils/calendarEventWithPeoplesCalendarInfo';
import { DateRange } from 'mini-datetime-utils/lib/DateRange';
import { getCalendarViewService } from 'mini-calendar-event-services/lib/services/getCalendarViewService';
import { getEarlierDate } from 'mini-datetime-utils/lib/dateTimeUtils';
import { LINKED_CALENDAR } from '../store/CalendarFolderSubscriptionIndex';
import { throwResponseError } from 'mini-calendar-event-services/lib/utils/throwResponseError';
import { traceAndReject } from 'error-handling';
import {
    addDays,
    differenceInDays,
    endOfDay,
    getDateString,
    userDate,
    utcDate,
} from 'owa-datetime';

/**
 * Returns the calendar events for a folder from the server
 * @param calendar calendar we need to fetch events
 * @param dateRange Date range for which events need to be fetched.
 */
export default async function getCalendarEventsFromServer(
    calendar: Calendar,
    dateRange: DateRange,
    appContext?: any
): Promise<CalendarEvent[]> {
    const {
        calculatedFolderId: folderId,
        id: { mailboxInfo },
    } = calendar;
    if (calendar.isFolderIDValid) {
        return getCalendarViewService(
            dateRange.start,
            dateRange.end,
            folderId,
            /**
             * For linked calendars, pass the explicitLogonEmail otherwise
             * the mailboxInfo will take care of it.
             */
            calendar.type === LINKED_CALENDAR ? calendar.SharedOwnerEmailAddress : null
        )
            .then((response: GetCalendarViewResponse) => {
                if (response && response.ResponseClass === 'Success') {
                    const calendarEvents = response.Items.map(calendarItem =>
                        calendarEventWithPeoplesCalendarInfo(calendarItem, mailboxInfo)
                    );

                    appContext &&
                        appContext.signal(datapoints.DAL.GCE_SERVICE.CALENDAR_EVENTS_RESPONSE, {
                            count: calendarEvents.length,
                            start: getDateString(userDate(dateRange.start)),
                            end: getDateString(userDate(dateRange.end)),
                        });

                    return calendarEvents;
                } else {
                    return throwResponseError(response);
                }
            })
            .catch(error =>
                traceAndReject('getCalendarEventsFromServer: getCalendarViewService', error)
            );
    } else {
        // Breaking the time interval into buckets of 42 days, to avoid server exceptions.
        // At max we will have 3 requests, because the max duration is currently 110.
        const dayLimit = 42;
        const noOfDays = differenceInDays(dateRange.end, dateRange.start);
        const promises = [
            createUserAvailabilityService(
                dateRange.start,
                getEarlierDate(dateRange.end, endOfDay(addDays(dateRange.start, dayLimit - 1))),
                calendar
            ),
            noOfDays > dayLimit &&
                createUserAvailabilityService(
                    addDays(dateRange.start, dayLimit),
                    getEarlierDate(
                        dateRange.end,
                        endOfDay(addDays(dateRange.start, 2 * dayLimit - 1))
                    ),
                    calendar
                ),
            noOfDays > 2 * dayLimit &&
                createUserAvailabilityService(
                    addDays(dateRange.start, 2 * dayLimit),
                    dateRange.end,
                    calendar
                ),
        ].filter(Boolean);

        return Promise.all(promises)
            .then((values: GetUserAvailabilityInternalResponse[]) => {
                const calendarEvents = [];
                for (const response of values) {
                    if (response && response.ResponseCode === 'NoError') {
                        calendarEvents.push(
                            ...response.Responses[0].CalendarView.Items.map(
                                // The response from getUserAvailabilityService comes in utc format. However the dates don't have the tz info.
                                // Hence adding the tz info by converting to utcDate so that further conversions to userDate happens correctly.
                                calendarItem =>
                                    calendarEventWithPeoplesCalendarInfo(
                                        {
                                            ...calendarItem,
                                            Start: utcDate(calendarItem.Start).toString(),
                                            End: utcDate(calendarItem.End).toString(),
                                        },
                                        mailboxInfo
                                    )
                            )
                        );
                    } else {
                        return throwResponseError(response);
                    }
                }
                return calendarEvents;
            })
            .catch(error => {
                trace.errorThatWillCauseAlert(
                    '[getCalendarEventsFromServer: getUserAvailabilityService]: ' + error
                );
                return [];
            });
    }
}

const createUserAvailabilityService = (start, end, calendar) =>
    getUserAvailabilityService({
        rangeStart: start,
        rangeEnd: end,
        attendees: [{ Mailbox: { EmailAddress: calendar?.SharedOwnerEmailAddress } }],
        requestedView: 'DetailedMerged',
        mailboxInfo: calendar?.id?.mailboxInfo,
    });
